import axios from "axios";

const baseURL =
  typeof process !== "undefined" && process.env.NEXT_PUBLIC_API_URL
    ? process.env.NEXT_PUBLIC_API_URL
    : "http://localhost:9999"; // unused when using rmcClient mock

export const api = axios.create({
  baseURL,
});

api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const token =
      window.localStorage.getItem("access_token") ||
      window.localStorage.getItem("token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (typeof window !== "undefined" && err.response?.status === 401) {
      window.location.replace("/signin");
    }
    return Promise.reject(err);
  }
);

/**
 * Reads API bodies like `{ success: false, error: { code, message } }` (optionally under `data`).
 * @param {unknown} body - Parsed JSON from `response.data` or equivalent.
 * @returns {string|null}
 */
export function extractApiEnvelopeMessage(body) {
  if (!body || typeof body !== "object" || Array.isArray(body)) return null;
  const read = (payload) => {
    if (!payload || typeof payload !== "object") return null;
    if (payload.success === false && payload.error && typeof payload.error === "object") {
      const m = payload.error.message;
      if (typeof m === "string" && m.trim()) return m.trim();
    }
    if (payload.error && typeof payload.error === "object") {
      const m = payload.error.message;
      if (typeof m === "string" && m.trim()) return m.trim();
    }
    return null;
  };
  return read(body) || read(body.data);
}

export function handleApiError(error) {
  if (error?.response == null) {
    if (error?.code === "ERR_NETWORK" || error?.message === "Network Error") {
      return "Network error — could not reach the server. Check NEXT_PUBLIC_API_URL, that the API is running, and HTTPS/mixed content if the app is on HTTPS.";
    }
    if (error?.request != null) {
      return "No response from server. Check the API base URL and that the backend is reachable.";
    }
    if (typeof error?.message === "string" && error.message.trim() && error.message !== "Error") {
      return error.message.trim();
    }
  }
  const data = error?.response?.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const envelopeMsg = extractApiEnvelopeMessage(data);
    if (envelopeMsg) return envelopeMsg;
    const fieldKeys = Object.keys(data).filter((k) => k !== "detail" && k !== "message");
    if (fieldKeys.length === 1) {
      const val = data[fieldKeys[0]];
      const flat =
        Array.isArray(val) && typeof val[0] === "string"
          ? val[0]
          : typeof val === "object" && val != null && Array.isArray(val.non_field_errors)
            ? val.non_field_errors[0]
            : Array.isArray(val)
              ? val[0]
              : null;
      if (typeof flat === "string" && flat) return `${fieldKeys[0]}: ${flat}`;
    }
  }
  const msg =
    data?.error?.message ||
    data?.message ||
    data?.detail ||
    (Array.isArray(data?.non_field_errors) ? data.non_field_errors[0] : null) ||
    (typeof data === "string" ? data : null);
  if (msg) return msg;
  const st = error?.response?.status;
  const stText = error?.response?.statusText;
  if (st != null) {
    return stText ? `Request failed (${st} ${stText}).` : `Request failed (${st}).`;
  }
  return "Something went wrong. Please try again.";
}
